import React, { useState, useEffect, useCallback } from "react";
import "./VersionHistoryDrawer.css";
import {
  getCommercialProposalApprovals,
  getProjectEstimateApprovals,
  restoreVersion,
} from "../../../../../api/projectEstimatesApi";
import { getApprovalStatusKind } from "../../../../Approvals/ProjectApproval/projectApprovalHelpers";

const VersionHistoryDrawer = ({
  open,
  onClose,
  projectId,
  type = "estimate",
  onVersionRestored,
}) => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [restoringVersion, setRestoringVersion] = useState(null);

  const loadApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Выбираем функцию в зависимости от типа
      const data =
        type === "commercial"
          ? await getCommercialProposalApprovals(projectId)
          : await getProjectEstimateApprovals(projectId);

      setApprovals(data);
    } catch (err) {
      console.error("Ошибка при загрузке истории согласований:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, type]);

  // Загружаем данные при открытии drawer
  useEffect(() => {
    if (open && projectId) {
      loadApprovals();
    }
  }, [open, projectId, loadApprovals]);

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ru-RU");
    } catch (error) {
      return dateString;
    }
  };

  // Функция для форматирования суммы
  const formatPrice = (price) => {
    return new Intl.NumberFormat("ru-RU").format(price);
  };

  // Функция для определения статуса согласования
  const getStatusText = (status) => {
    const kind = getApprovalStatusKind(status);
    if (kind === "approved") return "Согласован";
    if (kind === "rejected") return "Отклонен";
    if (kind === "cancelled") return "Отменен инициатором";
    if (kind === "pending") return "На рассмотрении";
    return status || "Неизвестно";
  };

  const getStatusColor = (status) => {
    const kind = getApprovalStatusKind(status);
    if (kind === "pending") return "#ffa500";
    if (kind === "approved") return "#28a745";
    if (kind === "rejected") return "#dc3545";
    return "#6c757d";
  };

  const getApprovalComments = (approval) => {
    const remarks = [];
    if (approval?.comment?.trim()) {
      remarks.push(approval.comment.trim());
    }
    (approval?.steps || []).forEach((step) => {
      if (step?.comment?.trim()) {
        remarks.push(
          `${step.approver_name || "Согласующий"}: ${step.comment.trim()}`,
        );
      }
    });
    return remarks;
  };

  // Функция для восстановления версии
  const handleRestoreVersion = async (version) => {
    if (!projectId) return;

    if (
      !window.confirm(
        "Вы уверены, что хотите восстановить эту версию? Все текущие изменения будут потеряны, а согласования будут сброшены."
      )
    ) {
      return;
    }

    try {
      setRestoringVersion(version);
      await restoreVersion(projectId, version);

      // Вызываем callback для обновления данных
      if (onVersionRestored) {
        onVersionRestored();
      }

      // Перезагружаем список согласований
      await loadApprovals();

      // Закрываем drawer и перезагружаем страницу
      onClose();
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error("Ошибка при восстановлении версии:", err);
      alert(`Ошибка при восстановлении версии: ${err.message}`);
    } finally {
      setRestoringVersion(null);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="version-drawer-backdrop" onClick={onClose} />
      <div className="version-drawer">
        <div className="version-drawer-header">
          <span className="version-drawer-title">
            История версий согласований
          </span>
          <button className="version-drawer-close" onClick={onClose}>
            &times;
          </button>
        </div>

        {loading ? (
          <div className="version-drawer-loading">
            <div>Загрузка данных...</div>
          </div>
        ) : error ? (
          <div className="version-drawer-error">
            <div>Ошибка загрузки: {error}</div>
            <button onClick={loadApprovals} className="retry-btn">
              Попробовать снова
            </button>
          </div>
        ) : (
          <div className="version-drawer-content">
            {approvals.length === 0 ? (
              <div className="version-drawer-empty">
                <div>История согласований пуста</div>
              </div>
            ) : (
              <table className="version-drawer-table">
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Дата</th>
                    <th>Отправитель</th>
                    <th>Сумма</th>
                    <th>Статус</th>
                    <th>Замечание</th>
                    <th>Версия</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {approvals.map((approval, idx) => {
                    const comments = getApprovalComments(approval);
                    return (
                    <tr key={approval.id}>
                      <td>{idx + 1}.</td>
                      <td>
                        <span className="version-drawer-date">
                          {formatDate(approval.date)}
                        </span>
                      </td>
                      <td>{approval.initiator_name}</td>
                      <td>{formatPrice(approval.total_price)} ₸</td>
                      <td>
                        <span
                          className="version-drawer-status"
                          style={{ color: getStatusColor(approval.status) }}
                        >
                          {getStatusText(approval.status)}
                        </span>
                      </td>
                      <td className="version-drawer-comment">
                        {comments.length > 0 ? comments.join("\n") : "—"}
                      </td>
                      <td>
                        {approval.version !== null &&
                        approval.version !== undefined
                          ? `v${approval.version}`
                          : "-"}
                      </td>
                      <td>
                        {approval.version !== null &&
                          approval.version !== undefined && (
                            <button
                              className="version-restore-btn"
                              onClick={() =>
                                handleRestoreVersion(approval.version)
                              }
                              disabled={restoringVersion === approval.version}
                              title="Восстановить эту версию"
                            >
                              {restoringVersion === approval.version
                                ? "Восстановление..."
                                : "Восстановить"}
                            </button>
                          )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default VersionHistoryDrawer;
